import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ArrowRight, Coffee } from "lucide-react";

const STORAGE_KEY = "magee_coffee_popup_session";
const IDLE_MS = 20_000; // Show after 20 seconds of inactivity

/**
 * CoffeePopup
 * Idle-triggered lead-magnet modal offering a $5 coffee gift card
 * in exchange for a booked consultation.
 *
 * Rules:
 *  - Shows once per browser SESSION (sessionStorage — resets when browser closes/reopens).
 *  - Never shows on /contact (they're already there).
 *  - Triggers after 20 seconds of user inactivity (mouse/scroll/keyboard/touch).
 *  - Also opens on-demand via `window.dispatchEvent(new Event("open-coffee-popup"))`.
 *  - Dismissible via X, backdrop click, or Escape.
 */
export default function CoffeePopup() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  // Idle-based trigger — resets on any user activity (works on desktop + mobile)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.pathname.startsWith("/contact")) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    let idleTimer;
    const arm = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => setVisible(true), IDLE_MS);
    };
    // Activity events — desktop + mobile
    const events = [
      "mousemove",
      "scroll",
      "keydown",
      "touchstart",
      "touchmove",
      "pointerdown",
      "click",
    ];
    events.forEach((ev) => window.addEventListener(ev, arm, { passive: true }));
    arm(); // start the initial timer

    return () => {
      clearTimeout(idleTimer);
      events.forEach((ev) => window.removeEventListener(ev, arm));
    };
  }, [location.pathname]);

  // Manual trigger from anywhere (e.g., footer "Coffee's on us" button)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const openNow = () => setVisible(true);
    window.addEventListener("open-coffee-popup", openNow);
    return () => window.removeEventListener("open-coffee-popup", openNow);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [visible]);

  const handleClose = () => {
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="coffee-popup-title"
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 md:p-6 animate-[coffeeFadeIn_.35s_ease]"
      data-testid="coffee-popup"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close offer"
        onClick={handleClose}
        className="absolute inset-0 bg-[#050A14]/70 backdrop-blur-md"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-lg rounded-3xl overflow-hidden animate-[coffeeSlideUp_.5s_cubic-bezier(.2,.9,.25,1)]"
      >
        {/* Animated gold conic border */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -inset-[1.5px] rounded-3xl opacity-90"
          style={{
            background:
              "conic-gradient(from 220deg at 50% 50%, rgba(214,167,72,0) 0deg, rgba(214,167,72,0.6) 120deg, rgba(255,225,150,0.95) 180deg, rgba(6,182,212,0.5) 260deg, rgba(214,167,72,0) 360deg)",
            animation: "coffeeSpin 9s linear infinite",
          }}
        />

        <div className="relative rounded-3xl bg-[#0A0A0D] text-white overflow-hidden">
          {/* Ambient glows */}
          <div className="pointer-events-none absolute -top-24 -left-16 w-[320px] h-[320px] rounded-full bg-amber-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -right-16 w-[320px] h-[320px] rounded-full bg-cyan-500/20 blur-3xl" />

          {/* Grain / grid texture */}
          <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />

          {/* Close */}
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            data-testid="coffee-popup-close"
            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>

          <div className="relative px-6 md:px-9 pt-8 md:pt-10 pb-7 md:pb-9">
            {/* Cup + steam mark */}
            <div className="flex items-center gap-4">
              <div className="relative w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 text-[#1A1204] flex items-center justify-center shadow-[0_10px_28px_-8px_rgba(214,167,72,0.55)]">
                <Coffee size={26} strokeWidth={2.2} />
                {/* Steam wisps */}
                <span
                  aria-hidden="true"
                  className="absolute -top-3 left-3 w-1 h-4 bg-white/60 rounded-full blur-[2px]"
                  style={{ animation: "coffeeSteamA 3s ease-in-out infinite" }}
                />
                <span
                  aria-hidden="true"
                  className="absolute -top-4 left-6 w-1 h-5 bg-white/50 rounded-full blur-[2px]"
                  style={{ animation: "coffeeSteamB 3.4s ease-in-out infinite" }}
                />
                <span
                  aria-hidden="true"
                  className="absolute -top-3 left-9 w-1 h-4 bg-white/60 rounded-full blur-[2px]"
                  style={{ animation: "coffeeSteamA 3.2s ease-in-out .4s infinite" }}
                />
              </div>

              <div>
                <p className="font-mono-brand text-[10px] uppercase tracking-[0.28em] text-amber-300">
                  On the house
                </p>
                <p className="mt-1 text-white/60 text-[13px]">
                  A little thank-you for spending time with us.
                </p>
              </div>
            </div>

            {/* Headline */}
            <h3
              id="coffee-popup-title"
              className="mt-6 font-editorial text-3xl md:text-4xl leading-[1.02] tracking-[-0.02em] text-balance"
            >
              Coffee&rsquo;s on us —{" "}
              <span className="italic font-medium bg-gradient-to-r from-amber-300 via-amber-200 to-cyan-300 bg-clip-text text-transparent">
                book a 15-min call.
              </span>
            </h3>

            <p className="mt-4 text-white/70 text-[15px] leading-relaxed">
              Grab a slot with our team. When we meet, we&rsquo;ll send a{" "}
              <span className="text-amber-300 font-medium">$5 coffee gift card</span>{" "}
              to your inbox — so the first round is on Magee.
            </p>

            {/* Perks strip */}
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-[11px] font-mono-brand uppercase tracking-[0.14em] text-white/80">
                15 minutes
              </span>
              <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-[11px] font-mono-brand uppercase tracking-[0.14em] text-white/80">
                No hard pitch
              </span>
              <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-[11px] font-mono-brand uppercase tracking-[0.14em] text-white/80">
                Gift card after
              </span>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to="/contact#book"
                onClick={handleClose}
                data-testid="coffee-popup-cta"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-[#1A1204] font-semibold text-[14px] shadow-[0_15px_35px_-10px_rgba(214,167,72,0.55)] hover:shadow-[0_20px_45px_-10px_rgba(214,167,72,0.8)] hover:translate-y-[-1px] transition-all duration-300"
              >
                Book my call
                <ArrowRight size={16} />
              </Link>
              <button
                type="button"
                onClick={handleClose}
                data-testid="coffee-popup-decline"
                className="text-white/60 hover:text-white text-[13px] font-mono-brand uppercase tracking-[0.18em] transition-colors py-2"
              >
                Maybe later
              </button>
            </div>

            <p className="mt-5 text-white/40 text-[11px] leading-relaxed">
              Gift card sent by email within 48 hours after the consultation.
              Valid for our prospective clients. One per person, please —
              we like coffee, not fraud.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
